import { BulletListIcon, CalendarIcon, PencilIcon, PersonGroupIcon } from '@navikt/aksel-icons';
import {
    ContextDataType,
    useContextComplete,
    useContextGetData,
    useContextSaveData,
} from 'appData/PlanleggerDataContext';
import { useEffektivHvemPlanlegger } from 'appData/useEffektivHvemPlanlegger';
import { usePlanleggerNavigator } from 'appData/usePlanleggerNavigator';
import { useStepData } from 'appData/useStepData';
import { FordelingSlider } from 'components/FordelingSlider';
import { useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { HvemPlanleggerType } from 'types/HvemPlanlegger';
import {
    erAlenesøker,
    erMedmorDelAvSøknaden,
    getEffektivHvemPlanlegger,
    getErFarEllerMedmor,
    getFornavnPåSøker1,
    getFornavnPåSøker2,
    getNavnPåForeldre,
} from 'utils/HvemPlanleggerUtils';
import { mapOmBarnetPlanleggerTilBarn } from 'utils/barnetUtils';
import { HvemHarRett, utledHvemSomHarRett, utledRettighet } from 'utils/hvemHarRettUtils';
import { getAntallUkerOgDagerFellesperiode } from 'utils/stønadskvoterUtils';
import { useLagUttaksplanForslag } from 'utils/useLagUttaksplanForslag';
import { finnAntallUkerOgDagerMedForeldrepenger, finnUttaksdata } from 'utils/uttakUtils';

import { BodyLong, BodyShort, Box, Heading, InlineMessage, Tabs, ToggleGroup, VStack } from '@navikt/ds-react';

import { loggUmamiEvent } from '@navikt/fp-observability';
import {
    Dekningsgrad,
    FordelingPlanlegger,
    KontoBeregningResultatDto,
    UttakPeriodeAnnenpartEøs_fpoversikt,
    UttakPeriode_fpoversikt,
} from '@navikt/fp-types';
import { BluePanel, Infobox, StepButtons } from '@navikt/fp-ui';
import { Uttaksperioden, compressToUrl, useMedia, useScrollBehaviour } from '@navikt/fp-utils';
import {
    FjernAltIUttaksplanModal,
    KvoteOppsummering,
    TilbakestillPlanModal,
    UttaksplanDataProvider,
    UttaksplanKalender,
    UttaksplanListe,
    UttaksplanRedigeringProvider,
} from '@navikt/fp-uttaksplan';
import { notEmpty } from '@navikt/fp-validation';

import { PlanleggerStepPage } from '../../components/page/PlanleggerStepPage';
import { barnehagestartDato } from '../barnehageplass/BarnehageplassSteg';
import { HvaErMulig } from './hva-er-mulig/HvaErMulig';
import { UforutsetteEndringer } from './uforutsette-endringer/UforutsetteEndringer';

interface Props {
    stønadskvoter: KontoBeregningResultatDto;
}

export const PlanenDeresSteg = ({ stønadskvoter }: Props) => {
    const intl = useIntl();
    const navigator = usePlanleggerNavigator();
    const stepConfig = useStepData();

    useScrollBehaviour();

    const hvemPlanlegger = notEmpty(useContextGetData(ContextDataType.HVEM_PLANLEGGER));
    const omBarnet = notEmpty(useContextGetData(ContextDataType.OM_BARNET));
    const hvorLangPeriode = notEmpty(useContextGetData(ContextDataType.HVOR_LANG_PERIODE));
    const arbeidssituasjon = notEmpty(useContextGetData(ContextDataType.ARBEIDSSITUASJON));
    const fordeling = useContextGetData(ContextDataType.FORDELING);
    const uttaksplan = useContextGetData(ContextDataType.UTTAKSPLAN);
    const completeAppContext = useContextComplete();

    const lagreUttaksplan = useContextSaveData(ContextDataType.UTTAKSPLAN);

    const stønadskvote100 = stønadskvoter['100'];
    const stønadskvote80 = stønadskvoter['80'];
    const valgtStønadskvote = hvorLangPeriode.dekningsgrad === '100' ? stønadskvote100 : stønadskvote80;
    const barnehagestartdato = barnehagestartDato(omBarnet);

    const isMedmorDelAvSøknaden = erMedmorDelAvSøknaden(hvemPlanlegger);
    const hvemHarRett = utledHvemSomHarRett(arbeidssituasjon);

    const erAleneOmOmsorg = erAlenesøker(hvemPlanlegger);

    const erFarEllerMedmor = getErFarEllerMedmor(hvemPlanlegger, hvemHarRett);
    const erDeltUttak = fordeling !== undefined;

    // For likekjønnede fedre er begge "far", så den valgte starterens identitet (søker1/søker2) må følge med i
    // navnefeltene (via effektiv hvemPlanlegger). For mor/far og mor/medmor er rollene faste, så vi bruker rådata og
    // lar rekkefølgen styres av starterForelder-taggen alene.
    const navnPåForeldre = getNavnPåForeldre(
        hvemPlanlegger.type === HvemPlanleggerType.FAR_OG_FAR
            ? getEffektivHvemPlanlegger(hvemPlanlegger, fordeling, omBarnet, intl)
            : hvemPlanlegger,
        intl,
    );

    const lagreUttaksplanOgOppdaterUrl = (
        oppdatertUttaksplan: Array<UttakPeriode_fpoversikt | UttakPeriodeAnnenpartEøs_fpoversikt> | undefined,
    ) => {
        lagreUttaksplan(oppdatertUttaksplan?.filter((p) => Uttaksperioden.erIkkeEøsPeriode(p)));

        const contextData = {
            ...completeAppContext,
            [ContextDataType.UTTAKSPLAN]: oppdatertUttaksplan,
        };

        const encodedData = compressToUrl(JSON.stringify(contextData));
        const currentPath = globalThis.location.pathname;
        const newUrl = `${currentPath}?data=${encodedData}`;

        globalThis.history.replaceState(null, '', newUrl);
    };

    const planforslag = useLagUttaksplanForslag(valgtStønadskvote);

    const kvoteOppsummeringRef = useRef<HTMLDivElement>(null);
    const scrollToKvoteOppsummering = () => {
        if (kvoteOppsummeringRef.current) {
            // Håndter spesielt for å unngå at element en scroller til blir liggende under headeren
            const elementTop = kvoteOppsummeringRef.current.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({
                top: elementTop - 80,
                behavior: 'smooth',
            });
        }
    };

    const farOgFarKunEnPartHarRett =
        hvemPlanlegger.type === HvemPlanleggerType.FAR_OG_FAR &&
        (hvemHarRett === 'kunSøker1HarRett' || hvemHarRett === 'kunSøker2HarRett');
    const farOgFarBeggeHarRett =
        hvemPlanlegger.type === HvemPlanleggerType.FAR_OG_FAR && hvemHarRett === 'beggeHarRett';
    const kombinertForslag = [...planforslag.søker1, ...planforslag.søker2];

    return (
        <PlanleggerStepPage steps={stepConfig} goToStep={navigator.goToNextStep}>
            <VStack gap="space-24">
                <Heading size="medium" spacing level="2">
                    <FormattedMessage id="OversiktSteg.Tittel" values={{ erAleneforsørger: erAleneOmOmsorg }} />
                </Heading>

                <UttaksplanDataProvider
                    barn={mapOmBarnetPlanleggerTilBarn(omBarnet)}
                    foreldreInfo={{
                        søker: erFarEllerMedmor ? 'FAR_MEDMOR' : 'MOR',
                        navnPåForeldre,
                        rettighetType: utledRettighet(erAleneOmOmsorg, erDeltUttak),
                        erMedmorDelAvSøknaden: isMedmorDelAvSøknaden,
                        erIkkeSøkerSpesifisert: erDeltUttak,
                        erFarOgFar: hvemPlanlegger.type === HvemPlanleggerType.FAR_OG_FAR,
                    }}
                    valgtStønadskvote={valgtStønadskvote}
                    harAktivitetskravIPeriodeUtenUttak={false}
                    uttakPerioder={uttaksplan ?? kombinertForslag}
                    erPeriodeneTilAnnenPartLåst={false}
                    erEndringssøknad={false}
                >
                    <div ref={kvoteOppsummeringRef}>
                        <KvoteOppsummering erInnsyn={false} visStatusIkoner />
                    </div>

                    <DereKanTilpassePlanenInfoBox erAleneforsørger={erAleneOmOmsorg} />

                    {farOgFarKunEnPartHarRett && omBarnet.erFødsel && <FarOgFarKunEnPartHarRettInfoBox />}
                    {farOgFarBeggeHarRett && omBarnet.erFødsel && <FarOgFarBeggeHarRettInfoBox />}

                    <HvaErMulig hvemPlanlegger={hvemPlanlegger} arbeidssituasjon={arbeidssituasjon} barnet={omBarnet} />

                    <UforutsetteEndringer
                        arbeidssituasjon={arbeidssituasjon}
                        hvemPlanlegger={hvemPlanlegger}
                        barnet={omBarnet}
                    />

                    <AntallUkerVelger
                        stønadskvoter={stønadskvoter}
                        hvemHarRett={hvemHarRett}
                        lagreUttaksplanOgOppdaterUrl={lagreUttaksplanOgOppdaterUrl}
                    />

                    <UttaksplanRedigeringProvider
                        oppdaterUttaksplan={lagreUttaksplanOgOppdaterUrl}
                        harEndretPlan={uttaksplan !== undefined}
                    >
                        <FjernAltIUttaksplanModal />
                        <TilbakestillPlanModal />

                        <Tabs
                            defaultValue="kalender"
                            onChange={(value) => {
                                loggUmamiEvent({
                                    origin: 'planlegger',
                                    eventName: 'tab klikk',
                                    eventData: {
                                        tittel: value,
                                    },
                                });
                            }}
                        >
                            <Tabs.List>
                                <Tabs.Tab
                                    value="kalender"
                                    label={<FormattedMessage id="PlanenDeresSteg.kalender" />}
                                    icon={<CalendarIcon aria-hidden />}
                                />
                                <Tabs.Tab
                                    value="liste"
                                    label={<FormattedMessage id="PlanenDeresSteg.liste" />}
                                    icon={<BulletListIcon aria-hidden />}
                                />
                            </Tabs.List>
                            <Tabs.Panel value="kalender" className="pt-5">
                                <UttaksplanKalender
                                    readOnly={false}
                                    barnehagestartdato={barnehagestartdato}
                                    scrollToKvoteOppsummering={scrollToKvoteOppsummering}
                                />
                            </Tabs.Panel>
                            <Tabs.Panel value="liste" className="pt-5">
                                <UttaksplanListe isReadOnly={false} />
                            </Tabs.Panel>
                        </Tabs>
                    </UttaksplanRedigeringProvider>
                </UttaksplanDataProvider>

                <StepButtons
                    goToPreviousStep={navigator.goToPreviousDefaultStep}
                    nextButtonOnClick={() => {
                        if (!uttaksplan) {
                            lagreUttaksplanOgOppdaterUrl(kombinertForslag);
                        }
                        navigator.goToNextDefaultStep();
                    }}
                    isJumpToEndButton
                    useSimplifiedTexts
                />
            </VStack>
        </PlanleggerStepPage>
    );
};

const FarOgFarKunEnPartHarRettInfoBox = () => (
    <Infobox
        header={<FormattedMessage id="OversiktSteg.Infoboks.FarOgFar.DereHarOppgitt" />}
        icon={
            <PersonGroupIcon height={24} width={24} fontSize="1.5rem" color="var(--ax-bg-accent-strong)" aria-hidden />
        }
        color="green"
    >
        <div>
            <BodyShort>
                <FormattedMessage id="OversiktSteg.Infoboks.FarOgFar.DenSomErBiologisk" />
            </BodyShort>
            <BodyShort>
                <FormattedMessage id="OversiktSteg.Infoboks.FarOgFar.HvisDetErStebarnsadopsjon" />
            </BodyShort>
        </div>
    </Infobox>
);
const FarOgFarBeggeHarRettInfoBox = () => (
    <Infobox
        header={<FormattedMessage id="OversiktSteg.Infoboks.FarOgFar.BeggeHarRett.DereHarOppgitt" />}
        icon={
            <PersonGroupIcon height={24} width={24} fontSize="1.5rem" color="var(--ax-bg-accent-strong)" aria-hidden />
        }
        color="green"
    >
        <div>
            <BodyShort>
                <FormattedMessage id="OversiktSteg.Infoboks.FarOgFar.DenSomErBiologisk" />
            </BodyShort>
            <BodyShort>
                <FormattedMessage id="OversiktSteg.Infoboks.FarOgFar.BeggeHarRett.BiologiskFar" />
            </BodyShort>
        </div>
    </Infobox>
);

const DereKanTilpassePlanenInfoBox = ({ erAleneforsørger }: { erAleneforsørger: boolean }) => (
    <Infobox
        header={<FormattedMessage id="OversiktSteg.Infoboks.Utkast" values={{ erAleneforsørger }} />}
        color="gray"
        icon={<PencilIcon height={24} width={24} fontSize="1-5rem" aria-hidden />}
    >
        <BodyLong>
            <FormattedMessage id="OversiktSteg.Infoboks.Utkast.Tekst" values={{ erAleneforsørger }} />
        </BodyLong>
    </Infobox>
);

const AntallUkerVelger = ({
    stønadskvoter,
    hvemHarRett,
    lagreUttaksplanOgOppdaterUrl,
}: {
    stønadskvoter: KontoBeregningResultatDto;
    hvemHarRett: HvemHarRett;
    lagreUttaksplanOgOppdaterUrl: (oppdatertUttaksplan: UttakPeriode_fpoversikt[] | undefined) => void;
}) => {
    const intl = useIntl();

    const omBarnet = notEmpty(useContextGetData(ContextDataType.OM_BARNET));
    const hvemPlanlegger = useEffektivHvemPlanlegger();
    const hvorLangPeriode = notEmpty(useContextGetData(ContextDataType.HVOR_LANG_PERIODE));
    const fordeling = useContextGetData(ContextDataType.FORDELING);

    const lagreFordeling = useContextSaveData(ContextDataType.FORDELING);
    const lagreHvorLangPeriodePlanlegger = notEmpty(useContextSaveData(ContextDataType.HVOR_LANG_PERIODE));

    const isDesktop = useMedia('screen and (min-width: 480)');

    const oppdaterPeriodeOgFordeling = (value: Dekningsgrad) => {
        const dekningsgrad = value;
        lagreHvorLangPeriodePlanlegger({ dekningsgrad });
        if (fordeling) {
            lagreFordeling({
                antallDagerSøker1: finnAntallDagerSøker1(dekningsgrad, stønadskvoter, fordeling),
            });
        }
        lagreUttaksplanOgOppdaterUrl(undefined);
    };

    const stønadskvote100 = stønadskvoter['100'];
    const stønadskvote80 = stønadskvoter['80'];
    const valgtStønadskvote = hvorLangPeriode.dekningsgrad === '100' ? stønadskvote100 : stønadskvote80;

    const antallUkerOgDager100 = finnAntallUkerOgDagerMedForeldrepenger(stønadskvote100);
    const antallUkerOgDager80 = finnAntallUkerOgDagerMedForeldrepenger(stønadskvote80);

    const fornavnSøker1 = getFornavnPåSøker1(hvemPlanlegger, intl);
    const fornavnSøker2 = getFornavnPåSøker2(hvemPlanlegger, intl);
    const antallUkerOgDagerFellesperiode = getAntallUkerOgDagerFellesperiode(valgtStønadskvote);

    const uttaksdata = finnUttaksdata(
        hvemHarRett,
        hvemPlanlegger,
        valgtStønadskvote,
        omBarnet,
        fordeling?.antallDagerSøker1,
    );

    return (
        <VStack gap="space-24">
            <ToggleGroup
                data-color="neutral"
                defaultValue={hvorLangPeriode?.dekningsgrad}
                size={isDesktop ? 'medium' : 'small'}
                onChange={(value) => oppdaterPeriodeOgFordeling(value as Dekningsgrad)}
                style={{ width: '100%' }}
            >
                <ToggleGroup.Item value={'100'}>
                    <FormattedMessage
                        id="OversiktSteg.100"
                        values={{
                            uker: antallUkerOgDager100.uker,
                            dager: antallUkerOgDager100.dager,
                        }}
                    />
                </ToggleGroup.Item>
                <ToggleGroup.Item value={'80'}>
                    <FormattedMessage
                        id="OversiktSteg.80"
                        values={{
                            uker: antallUkerOgDager80.uker,
                            dager: antallUkerOgDager80.dager,
                        }}
                    />
                </ToggleGroup.Item>
            </ToggleGroup>
            {hvemHarRett === 'beggeHarRett' &&
                (!omBarnet.erFødsel || hvemPlanlegger.type !== HvemPlanleggerType.FAR_OG_FAR) && (
                    <>
                        <BluePanel>
                            <Heading id="fordeling-slider-label" size="small" level="3">
                                <FormattedMessage id="PlanenDeresSteg.FordelingTittel" />
                            </Heading>
                            <BodyShort className="mb-4">
                                <FormattedMessage id="PlanenDeresSteg.Undertekst" />
                            </BodyShort>
                            <FordelingSlider
                                antallDagerSøker1={fordeling?.antallDagerSøker1}
                                onAntallDagerSøker1Change={(value) => {
                                    lagreFordeling({ antallDagerSøker1: value });
                                    lagreUttaksplanOgOppdaterUrl(undefined);
                                }}
                                antallUkerOgDagerFellesperiode={antallUkerOgDagerFellesperiode}
                                fornavnSøker1={fornavnSøker1}
                                fornavnSøker2={fornavnSøker2}
                                uttaksdata={uttaksdata}
                            />
                        </BluePanel>
                        {antallUkerOgDagerFellesperiode.dager > 0 && (
                            <Box paddingInline="space-16">
                                <InlineMessage status="info">
                                    <BodyShort size="small" className="text-text-subtle">
                                        {antallUkerOgDagerFellesperiode.dager === 1 ? (
                                            <FormattedMessage id="PlanenDeresSteg.EkstraDagInfo.EnDag" />
                                        ) : (
                                            <FormattedMessage id="PlanenDeresSteg.EkstraDagInfo.FlereDager" />
                                        )}
                                    </BodyShort>
                                </InlineMessage>
                            </Box>
                        )}
                    </>
                )}
        </VStack>
    );
};

const finnAntallDagerSøker1 = (
    dekningsgrad: Dekningsgrad,
    stønadskvoter: KontoBeregningResultatDto,
    fordeling: FordelingPlanlegger,
) => {
    const ukerOgDagerFellesperiode = getAntallUkerOgDagerFellesperiode(
        dekningsgrad === '100' ? stønadskvoter['100'] : stønadskvoter['80'],
    );

    return Math.min(fordeling.antallDagerSøker1, ukerOgDagerFellesperiode.totaltAntallDager);
};
